import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom/client";
import { createHashRouter, RouterProvider } from "react-router-dom";
import { I18nextProvider } from "react-i18next";
import { HelmetProvider } from "react-helmet-async";
import { initializeIcons } from "@fluentui/react";

import "./index.css";

import Chat from "./pages/chat/Chat";
import AdminConsolePage from "./pages/admin/AdminConsolePage";
import LayoutWrapper from "./layoutWrapper";
import i18next from "./i18n/config";
import { LoadingScreen } from "./components/LoadingScreen";
import { initializeAuthSetup, _updateExportsAfterInit, checkIsUsingAppServicesLogin, _updateIsUsingAppServicesLogin } from "./authConfig";

const LOGOUT_HARD_REFRESH_PARAM = "logoutHardRefresh";
const LOGOUT_HARD_REFRESH_SENTINEL = "__atlas_logout_hard_refresh__";

const shouldRenderApp = (() => {
    try {
        const currentUrl = new URL(window.location.href);
        if (currentUrl.searchParams.has(LOGOUT_HARD_REFRESH_PARAM)) {
            if (window.name === LOGOUT_HARD_REFRESH_SENTINEL) {
                window.name = "";
                window.history.replaceState(null, "", window.location.origin);
                return true;
            }
            window.name = LOGOUT_HARD_REFRESH_SENTINEL;
            window.location.reload();
            return false;
        }

        if (window.name === LOGOUT_HARD_REFRESH_SENTINEL) {
            window.name = "";
            window.location.replace(window.location.origin);
            return false;
        }
    } catch (error) {
        console.warn("[Auth] Failed to apply logout hard refresh handling", error);
    }
    return true;
})();

initializeIcons();

// App component that handles async initialization
const App = () => {
    const [isInitialized, setIsInitialized] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [retryCount, setRetryCount] = useState(0);

    useEffect(() => {
        const initialize = async () => {
            try {
                console.log("[App] Starting async initialization...");

                // Initialize auth setup without blocking
                const authSetup = await initializeAuthSetup();
                _updateExportsAfterInit(authSetup);
                console.log("[App] Auth setup initialized");

                // Check if using App Services login (don't block on this)
                checkIsUsingAppServicesLogin()
                    .then(isUsing => {
                        _updateIsUsingAppServicesLogin(isUsing);
                        console.log("[App] App Services login check completed:", isUsing);
                    })
                    .catch(err => {
                        console.warn("[App] App Services login check failed (non-critical):", err);
                        _updateIsUsingAppServicesLogin(false);
                    });

                console.log("[App] Initialization complete");
                setIsInitialized(true);
            } catch (err) {
                console.error("[App] Initialization failed:", err);
                setError(err instanceof Error ? err.message : "Failed to initialize application");
            }
        };

        initialize();
    }, [retryCount]);

    if (error) {
        return (
            <LoadingScreen
                message="Failed to load application"
                showTimeoutWarning={true}
                onRetry={() => {
                    setError(null);
                    setRetryCount(prev => prev + 1);
                }}
            />
        );
    }

    if (!isInitialized) {
        return <LoadingScreen message="Initializing..." showTimeoutWarning={true} />;
    }

    const router = createHashRouter([
        {
            path: "/",
            element: <LayoutWrapper />,
            children: [
                {
                    index: true,
                    element: <Chat />
                },
                {
                    path: "admin/dev-console",
                    element: <AdminConsolePage />
                },
                {
                    path: "*",
                    lazy: () => import("./pages/NoPage")
                }
            ]
        }
    ]);

    return (
        <React.StrictMode>
            <I18nextProvider i18n={i18next}>
                <HelmetProvider>
                    <RouterProvider router={router} />
                </HelmetProvider>
            </I18nextProvider>
        </React.StrictMode>
    );
};

if (shouldRenderApp) {
    ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(<App />);
}
